import { Component } from "react";
import _debounce from "lodash/debounce";
import { Form, Field, FormSpy } from "react-final-form";
import InputText from "@components/Form/InputText";
import InputCheckbox from "@components/Form/Checkbox";
import Select from "@components/Form/Select";
import Loading from "@components/Loading";
import { getLoanSimulation } from "@store/financing-solutions/actions";
import {
  isRequired,
  composeFormValidators,
  isAlphaNumeric,
  isNumber,
  isPhoneNumber,
  isEmail,
  hasMaxLength,
  isInRange,
  hasMinLength,
} from "@lib/validations";
import { connect } from "react-redux";
import Heading from "@components/Heading";
import Button from "@components/Button";
import getConfig from "next/config";
import Alert from "@components/Alert";

const {
  publicRuntimeConfig: {
    credex,
    settings: { currency, baseUrl, websiteName },
  },
} = getConfig();

class OnlineCreditRequestForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      rate: undefined,
      isSent: false,
      isSending: false,
      installments: null,
      credexLoanSimulation: null,
      amount: credex.calculator.defaultAmount,
    };

    this.formReset = undefined;
  }

  async componentDidMount() {
    this.getLoanSimulation(this.state.amount, null);
  }

  getLoanSimulation = async (
    amount = credex.calculator.defaultAmount,
    installmentsNumber = null
  ) => {
    const loanSimulation = await this.props.getLoanSimulation({
      amount: amount,
      method: ["credex"],
    });

    if (loanSimulation && !loanSimulation.error) {
      if (
        loanSimulation.data.credex &&
        Array.isArray(loanSimulation.data.credex) &&
        loanSimulation.data.credex.length !== 0
      ) {
        this.setState({
          installments:
            installmentsNumber || loanSimulation.data.credex[0].installment_no,
          amount,
          credexLoanSimulation: loanSimulation.data.credex,
          rate: installmentsNumber
            ? loanSimulation.data.credex.find(
                ({ installment_no }) => installment_no == installmentsNumber
              ).installment_value
            : loanSimulation.data.credex[0].installment_value,
        });
      } else {
        this.setState({
          rate: 0,
        });
      }
    }
  };

  updateFormState = _debounce((subscription) => {
    const { errors, values, active } = subscription;

    if (active && this.state.isSent) {
      this.setState({
        isSent: false,
      });
    }

    // Set amount and installments number
    // if we don't have validation errors on these fields
    const amount = !errors.amount ? values.amount : null;
    const installmentsNumber = !errors.installments
      ? values.installments
      : null;

    if (
      (amount && this.state.amount != amount) ||
      (installmentsNumber && installmentsNumber != this.state.installments)
    )
      this.getLoanSimulation(Number(amount), Number(installmentsNumber));
  }, 500);

  handleFormSubmit = async (values) => {
    const { customer } = this.props;

    this.setState({
      isSent: false,
      isSending: true,
    });

    const response = await this.props.onFormSubmit({
      firstname: values.first_name,
      lastname: values.last_name,
      email: values.email,
      phone: values.phone,
      amount: values.amount,
      rates: values.installments,
    });

    if (response) {
      this.setState({
        isSent: true,
        isSending: false,
        rate: undefined,
        amount: credex.calculator.defaultAmount,
      });

      this.formReset({
        first_name: customer && customer.first_name,
        last_name: customer && customer.last_name,
        email: customer && customer.email,
        phone: customer && customer.telephone,
        amount: credex.calculator.defaultAmount,
        installments: this.state.credexLoanSimulation[0],
        agree_terms: false,
      });
    } else {
      this.setState({
        isSent: false,
        isSending: false,
      });
    }
  };

  render() {
    const { customer } = this.props;
    const {
      rate,
      isSent,
      isSending,
      credexLoanSimulation,
      installments,
      amount,
    } = this.state;

    // Show calculator if we have data
    if (
      credex.calculator.defaultAmount &&
      credex.calculator.minAmount &&
      credex.calculator.maxAmount
    ) {
      return (
        <>
          <Heading
            as="h2"
            display="h2"
            title="Calculator rate"
            classesProp="mb-3 lg:mb-1 -tracking-0.5 lg:-tracking-0.67"
          />

          <p className="mb-5 sm:mb-15px text-sm leading-20 -tracking-0.29 lg:text-base lg:leading-22 lg:-tracking-0.39">
            Completeaza valoarea produselor, selecteaza numarul de rate dorit si
            afla rata lunara:
          </p>

          <Form
            onSubmit={(values) => this.handleFormSubmit(values)}
            initialValues={{
              first_name: customer && customer.first_name,
              last_name: customer && customer.last_name,
              email: customer && customer.email,
              phone: customer && customer.telephone,
              amount: amount,
              installments: installments,
            }}
            render={({ handleSubmit, form }) => {
              this.formReset = form.reset;

              const tipMessage = credexLoanSimulation?.find((loanSimulation) =>
                Boolean(loanSimulation.additional_data)
              );

              return (
                <form onSubmit={handleSubmit} noValidate={true}>
                  <FormSpy
                    onChange={this.updateFormState}
                    subscription={{
                      active: true,
                      values: true,
                      errors: true,
                    }}
                  />

                  <div className="xl:flex xl:-mx-2">
                    <div className="xl:w-1/2 xl:px-2 mb-4 xl:mb-0">
                      <Field
                        component={InputText}
                        id="online_credit_request_amount"
                        name="amount"
                        label="Valoarea totala a produselor"
                        inputClassName="max-w-full"
                        labelClass="leading-18.2"
                        type="text"
                        validateOn="active"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isNumber(),
                          isInRange(
                            credex.calculator.minAmount,
                            credex.calculator.maxAmount,
                            `Oferta valabila de la ${credex.calculator.minAmount} ${currency} la ${credex.calculator.maxAmount} ${currency}`
                          )
                        )}
                      />
                    </div>

                    <div className="xl:w-1/2 xl:px-2 mb-4 xl:mb-0">
                      <Field
                        id="online_credit_request_installments"
                        name="installments"
                        component={Select}
                        label="Numar de rate"
                        inputClassName="max-w-full"
                        labelClass="leading-18.2"
                        // If installments array contains
                        // only one installment disable select
                        disabled={credexLoanSimulation?.length === 1}
                      >
                        {credexLoanSimulation?.map((month) => (
                          <option
                            key={month.installment_no}
                            value={month.installment_no}
                          >
                            {month.installment_no}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>

                  {tipMessage && (
                    <span className="flex items-center justify-start bg-yellow-300 shadow-inner rounded-md w-full mt-5 px-4 py-3">
                      <p className="inline-block">
                        {
                          tipMessage.additional_data.message.split(
                            "{{value}}"
                          )[0]
                        }
                        <strong>{tipMessage.additional_data.value}</strong>
                        {
                          tipMessage.additional_data.message.split(
                            "{{value}}"
                          )[1]
                        }
                      </p>
                    </span>
                  )}

                  {!isNaN(rate) && (
                    <div className="mt-3 mb-6 pb-6 lg:pb-8 lg:mb-8 border-b text-center lg:mt-6">
                      {rate > 0 ? (
                        <>
                          <Heading
                            as="h2"
                            display="h4"
                            title="Rata lunara va fi de:"
                            classesProp="md:inline-block text-2xl -tracking-0.5 lg:-tracking-0.54 lg:text-26px leading-none"
                            font="medium"
                          />
                          <Heading
                            as="h2"
                            display="h4"
                            title={
                              <span className="text-red-brand ml-2 md:ml-4">
                                {rate} {currency}
                              </span>
                            }
                            classesProp="mt-3 md:mt-0 md:inline-block text-2xl -tracking-0.5 lg:-tracking-0.54 lg:text-26px leading-none"
                            font="medium"
                          />
                          <small className="mt-3 md:mt-0 block text-red-brand text-[11px] leading-[14.3px]">
                            Valoarea ratei afisata este orientativa si nu are
                            valoare contractuala.
                          </small>
                        </>
                      ) : (
                        <Heading
                          as="h2"
                          display="h4"
                          title="Momentan nu am putut calcula valoarea ratei lunare."
                          classesProp="md:inline-block text-2xl -tracking-0.5 lg:-tracking-0.54 lg:text-26px leading-none"
                          font="medium"
                        />
                      )}
                    </div>
                  )}

                  <div className="xl:flex xl:-mx-2">
                    <div className="xl:w-1/2 xl:px-2 mb-4">
                      <Field
                        component={InputText}
                        id="online_credit_request_last_name"
                        name="last_name"
                        label="Nume"
                        inputClassName="max-w-full"
                        labelClass="leading-18.2"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isAlphaNumeric(),
                          hasMinLength(3)
                        )}
                      />
                    </div>

                    <div className="xl:w-1/2 xl:px-2 mb-4">
                      <Field
                        component={InputText}
                        id="online_credit_request_first_name"
                        name="first_name"
                        label="Prenume"
                        inputClassName="max-w-full"
                        labelClass="leading-18.2"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isAlphaNumeric(),
                          hasMinLength(3)
                        )}
                      />
                    </div>
                  </div>

                  <div className="xl:flex xl:-mx-2">
                    <div className="xl:w-1/2 xl:px-2 mb-4">
                      <Field
                        component={InputText}
                        id="online_credit_request_phone"
                        name="phone"
                        label="Telefon"
                        inputClassName="max-w-full"
                        labelClass="leading-18.2"
                        placeholder="Numar de telefon"
                        type="tel"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isPhoneNumber()
                        )}
                      />
                    </div>

                    <div className="xl:w-1/2 xl:px-2 mb-4">
                      <Field
                        component={InputText}
                        id="online_credit_request_email"
                        name="email"
                        label="Introdu adresa de email"
                        inputClassName="max-w-full"
                        labelClass="leading-18.2"
                        type="email"
                        required
                        validate={composeFormValidators(
                          isRequired(),
                          isEmail(),
                          hasMaxLength(80)
                        )}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <Field
                      name="agree_terms"
                      label={
                        <>
                          Sunt de acord cu{" "}
                          <a
                            href={`${baseUrl}/suport-clienti/informatii-generale-termeni-si-conditii/`}
                            title="Termenii si conditiile"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Termenii si conditiile
                          </a>{" "}
                          de utilizare a site-ului{" "}
                          {websiteName}
                          .ro
                        </>
                      }
                      type="checkbox"
                      component={InputCheckbox}
                      required
                      validate={isRequired()}
                    />
                  </div>

                  <div className="lg:w-232px mx-auto mt-6 lg:mt-12">
                    <Button
                      type="submit"
                      display="block"
                      variant="primary"
                      title="Vreau sa fiu contactat"
                    />
                  </div>

                  {isSending && <Loading height={80}>Se trimite</Loading>}
                </form>
              );
            }}
          />

          {/* Sent form */}
          {isSent && (
            <div className="u-space-mb-20 u-text-align-center">
              <Alert type="success" showIcon>
                Formularul a fost trimis.
              </Alert>
            </div>
          )}
        </>
      );
    }

    // No data available, fallback
    return (
      <div className="u-space-p-30">
        <h1>Calculatorul de credit este momentan indisponibil.</h1>
      </div>
    );
  }
}

export default connect(null, { getLoanSimulation })(OnlineCreditRequestForm);
